import {
  Component,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  EventEmitter,
  ViewChild,
  inject,
} from '@angular/core';
import { startWith, takeUntil, tap } from 'rxjs/operators';
import { InvestmentWizardService } from '../../../investment-wizard/services/investment-wizard-service.service';
import { FormService } from '../../form-service.service';
import { Subject, map } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FiveTwoNine } from '../../../models/iw-models';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { AsyncPipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-five-two-nine-picker',
  templateUrl: './five-two-nine-picker.component.html',
  styleUrl: './five-two-nine-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    MatSelectModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatInputModule,
  ],
})
export class FiveTwoNinePickerComponent
  implements OnInit, OnChanges, OnDestroy
{
  private iws = inject(InvestmentWizardService);
  private changeDetector = inject(ChangeDetectorRef);
  private formSvc = inject(FormService);

  @ViewChild('fiveTwoNineField') public fiveTwoNineField;

  @Input({ required: true }) form;
  @Input({ required: true }) program;
  @Input() existingPlan;
  @Input() clear;
  @Output() selectedPlan = new EventEmitter();

  fiveTwoNineControl = new FormControl('');
  programId: string;
  internalForm = {};
  plans529: FiveTwoNine[] = [];
  filtered529s;
  unsubscribe = new Subject();
  selected = null;

  ngOnInit(): void {
    this.programId = this.program;
    if (this.existingPlan) {
      this.fiveTwoNineControl.setValue(this.existingPlan.PlanId);
    }
    this.get529(true);

    this.formSvc.clear529PickerData$.pipe(tap()).subscribe(() => {
      this.selectedPlan.emit({});
      this.changeDetector.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form) {
      this.internalForm = { ...changes.form.currentValue };
    }

    if (changes.existingPlan) {
      this.fiveTwoNineControl.setValue(changes.existingPlan.currentValue);
    }

    if (
      changes.program &&
      this.programId &&
      changes.program.currentValue !== this.programId
    ) {
      this.plans529 = [];
      this.programId = changes.program.currentValue;
      this.get529(true);
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  updateOptions() {
    this.filtered529s = this.fiveTwoNineControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        return this._filter(value || '');
      })
    );
  }

  get529(initialLoad = false) {
    this.iws
      .get529(true, this.programId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(x => {
        if (x['data']) {
          this.plans529 = [...x['data']];
        }
        if (this.existingPlan) {
          this.fiveTwoNineControl.setValue(this.existingPlan);
        }
        if (initialLoad) {
          this.updateOptions();
        }
        this.changeDetector.detectChanges();
      });
  }

  handle529(ev) {
    const plan = this.plans529.find(x => x.PlanId === ev.option.value);
    this.selectedPlan.emit(plan);
  }

  // Had to use the fat arrow to allow it to have access to 'this'.
  // Otherwise 'this' would be bound to the autocomplete component.
  displayFn = id => {
    const selected = this.plans529.find(x => x.PlanId === id);
    return selected && selected.PlanName ? selected.PlanName : '';
  };

  private _filter(value: string): FiveTwoNine[] {
    const filterValue = value.toLowerCase();

    return this.plans529.filter(option =>
      option.PlanName.toLowerCase().includes(filterValue)
    );
  }
}
